import { atomWithStorage } from 'jotai/utils';
import { Atom, atom, Getter } from 'jotai';
import { LinkConfig, SelectedNavigation } from '@/types/navigation';
import { NavigationLinkMapping } from '@statsbomb/parachute-types';
import { KeysToCamelCase } from '@/types/generic';
import {
  playerNavigationGameLinkIdAtom,
  playerNavigationCompetitionLinkIdAtom,
  playerNavigationKeeperLinkIdAtom,
  playerNavigationTeamLinkIdAtom,
  playerIdMappingAtom,
} from './queries/players';
import {
  teamNavigationGameLinkIdAtom,
  teamNavigationCompetitionLinkIdAtom,
  teamNavigationKeeperLinkIdAtom,
  teamNavigationPlayerLinkIdAtom,
  userDefaultTeamIdsAtom,
} from './queries/teams';
import { globalPlayerIdAtom } from './player/player';

export const navigationOpenAtom = atomWithStorage('navigationOpen', false);

export const selectedNavigationAtom = atom<SelectedNavigation>('crossPlatform');

export const playerPageLink: LinkConfig = {
  path: '/:language/player/:playerId/dashboard',
  relative: 'path',
  params: [],
  removeOtherParams: true,
};

export const playerPreviewLink: LinkConfig = {
  path: 'player/preview',
  relative: 'route',
  params: ['playerId'],
};

export const videoPreviewPageLink: LinkConfig = {
  path: 'video/preview',
  relative: 'route',
  params: [],
};
export const videoPreviewPagePathLink: LinkConfig = {
  path: 'video/preview',
  relative: 'path',
  params: [],
};

export const previewPageLink: LinkConfig = {
  path: 'preview',
  relative: 'route',
  params: [],
};

// don't go anywhere
export const samePageLink: LinkConfig = {
  path: '',
  relative: 'route',
  params: [],
};

// go to the current page relative to the component route
export const routeCurrentPageLink: LinkConfig = {
  path: '.',
  relative: 'route',
  params: [],
};

const visVideoPreviewLink: LinkConfig = {
  path: '/:language/data-locker/vis/video/preview',
  relative: 'path',
  params: [],
};

export const playerLinkAtom = atom<LinkConfig>(playerPageLink);

export const eventLinkAtom = atom<LinkConfig>(previewPageLink);

export const videoLinkAtom = atom<LinkConfig>(videoPreviewPageLink);

export const displaySettingsLinkAtom = atom<LinkConfig>(samePageLink);

export const visVideoLinkAtom = atom<LinkConfig>(visVideoPreviewLink);

const navigationId = async (
  get: Getter,
  playerNavigationLinkIdAtom: Atom<Promise<KeysToCamelCase<NavigationLinkMapping>>>,
  teamNavigationLinkIdAtom: Atom<Promise<KeysToCamelCase<NavigationLinkMapping>>>,
) => {
  const playerNavigationLinkId = (await get(playerNavigationLinkIdAtom)).iqId;
  if (playerNavigationLinkId) return playerNavigationLinkId;
  const teamNavigationLinkId = (await get(teamNavigationLinkIdAtom)).iqId;
  return teamNavigationLinkId || 0;
};

export const navigationGameIdAtom = atom(async get =>
  navigationId(get, playerNavigationGameLinkIdAtom, teamNavigationGameLinkIdAtom),
);

export const navigationCompetitionIdAtom = atom(async get =>
  navigationId(get, playerNavigationCompetitionLinkIdAtom, teamNavigationCompetitionLinkIdAtom),
);

export const navigationKeeperIdAtom = atom(async get =>
  navigationId(get, playerNavigationKeeperLinkIdAtom, teamNavigationKeeperLinkIdAtom),
);

export const userDefaultTeamIqIdAtom = atom(async get => (await get(userDefaultTeamIdsAtom))?.iqId || 0);

export const navigationTeamIdAtom = atom(async get => {
  const selectedPlayerTeamId = await get(playerNavigationTeamLinkIdAtom);
  return selectedPlayerTeamId.iqId ? selectedPlayerTeamId.iqId : get(userDefaultTeamIqIdAtom);
});

/**
 * Unlike the other navigation id atoms, this one returns both the DataHub ID and the IQ ID, since the
 * one we need is dependent upon the user's chosen experience.
 */
export const navigationPlayerIdAtom = atom(async get => {
  const playerIdMapping = await get(playerIdMappingAtom);
  const selectedPlayerId = get(globalPlayerIdAtom);
  if (selectedPlayerId)
    return {
      datahubId: selectedPlayerId,
      iqId: selectedPlayerId ? playerIdMapping[selectedPlayerId] : null,
    };
  return get(teamNavigationPlayerLinkIdAtom);
});
